/**
 * HustleEventSuccessPage — post-Stripe-checkout landing page.
 *
 * The Stripe success_url looks like:
 *   /hustle-events/success?session_id=cs_test_…&event=<slug>
 *
 * Flow:
 *   1. Poll `GET /api/hustle-events/tickets?session_id=…` for up to
 *      ~15s while the webhook is processing.
 *   2. Once tickets exist, render each with Add-to-Apple-Wallet /
 *      Add-to-Google-Wallet / Add-to-Google-Calendar / .ics buttons
 *      hitting the universal `/api/wallet-pass/event_ticket/…` and
 *      `/api/calendar/event_ticket/…` endpoints.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Ticket, CheckCircle2, Wallet, Calendar as CalIcon } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

async function pollTickets(sessionId, maxMs = 20000) {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    try {
      const { data } = await axios.get(
        `${API}/hustle-events/tickets`,
        { params: { session_id: sessionId }, withCredentials: true },
      );
      const list = data?.tickets || [];
      if (list.length > 0) return list;
    } catch { /* keep polling */ }
    await new Promise((res) => setTimeout(res, 1200));
  }
  return [];
}

export default function HustleEventSuccessPage() {
  const [params] = useSearchParams();
  const sid = params.get("session_id") || "";
  const { t, i18n } = useTranslation();
  const [tickets, setTickets] = useState(null);

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "HustleEvents — Payment confirmed";
    if (!sid) { setTickets([]); return; }
    pollTickets(sid).then(setTickets);
  }, [sid, i18n.language]);

  return (
    <div
      data-testid="hustle-event-success-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleEvents", to: "/hustle-events" }}
        nav={[{ to: "/hustle-events", label: t("events_nav_catalog", { defaultValue: "Events" }) }]}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 text-center">
        <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
        <h1 className="mt-4 text-3xl md:text-4xl font-black">
          {t("events_success_title", { defaultValue: "You're in." })}
        </h1>
        <p className="mt-2 text-stone-500">
          {t("events_success_sub", {
            defaultValue: "Payment confirmed. Add your ticket to Wallet or Calendar below.",
          })}
        </p>

        {tickets === null && (
          <div className="mt-8 text-stone-400" data-testid="events-success-loading">
            {t("events_success_loading", { defaultValue: "Issuing your ticket…" })}
          </div>
        )}
        {tickets && tickets.length === 0 && (
          <div className="mt-8 text-stone-400" data-testid="events-success-empty">
            {t("events_success_empty", {
              defaultValue: "We couldn't find your ticket yet. Try refreshing in a moment.",
            })}
          </div>
        )}
        {tickets && tickets.length > 0 && (
          <div className="mt-8 space-y-4">
            {tickets.map((tk) => (
              <div key={tk.id}
                data-testid={`ticket-card-${tk.id}`}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 text-left"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Ticket size={18} className="text-amber-500" />
                  <span className="uppercase tracking-widest text-[10px] text-stone-500">
                    Ticket · {tk.id.slice(-8).toUpperCase()}
                  </span>
                </div>
                <p className="font-black text-lg">{tk.seat_label || "General admission"}</p>
                <p className="text-sm text-stone-500 mt-1">{tk.buyer_name}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={`${API}/wallet-pass/event_ticket/${tk.id}/apple`}
                    data-testid={`wallet-apple-${tk.id}`}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-bold bg-slate-950 text-white hover:bg-slate-800"
                  >
                    <Wallet size={14} /> {t("events_wallet_apple", { defaultValue: "Apple Wallet" })}
                  </a>
                  <a
                    href={`${API}/wallet-pass/event_ticket/${tk.id}/google`}
                    data-testid={`wallet-google-${tk.id}`}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-bold border border-stone-300 dark:border-stone-700 hover:border-amber-500"
                  >
                    <Wallet size={14} /> {t("events_wallet_google", { defaultValue: "Google Wallet" })}
                  </a>
                  <a
                    href={`${API}/calendar/event_ticket/${tk.id}.ics`}
                    data-testid={`cal-ics-${tk.id}`}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-bold border border-stone-300 dark:border-stone-700 hover:border-amber-500"
                  >
                    <CalIcon size={14} /> {t("events_cal_ics", { defaultValue: ".ics file" })}
                  </a>
                  <a
                    href={`${API}/calendar/event_ticket/${tk.id}/google`}
                    data-testid={`cal-google-${tk.id}`}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-bold border border-stone-300 dark:border-stone-700 hover:border-amber-500"
                  >
                    <CalIcon size={14} /> {t("events_cal_google", { defaultValue: "Google Calendar" })}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <Link
          to="/hustle-events"
          className="mt-10 inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold border border-stone-300 dark:border-stone-700 hover:border-amber-500"
          data-testid="events-success-browse-more"
        >
          {t("events_success_browse", { defaultValue: "Browse more events" })}
        </Link>
      </div>
      <SuiteFooter trivagoContext="events" />
    </div>
  );
}
